import { Appointment } from '../types/appointments';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../lib/firebase';
import { getAuth } from 'firebase/auth';

// Firebase Functions típusok
interface CreateMeetingRequest {
  appointmentId: string;
  organizationId: string;
  clientName: string;
  clientEmail: string;
  organizerEmail: string;
  appointmentDate: string;
  duration: number;
  title?: string;
  description?: string;
  clientPhone?: string;
  clientMessage?: string;
}

interface UpdateMeetingRequest {
  appointmentId: string;
  calendarEventId: string;
  organizerEmail: string;
  clientName: string;
  clientEmail: string;
  appointmentDate: string;
  duration: number;
  title?: string;
  description?: string;
  clientPhone?: string;
  clientMessage?: string;
}

interface DeleteMeetingRequest {
  calendarEventId: string;
  organizerEmail: string;
}

interface GoogleMeetResponse {
  success: boolean;
  eventId: string;
  meetLink: string;
  calendarLink: string;
  message: string;
}

export interface GoogleCalendarStatus {
  configured: boolean;      // van-e OAuth client beállítva a szerveren
  connected: boolean;
  email?: string | null;
  connectedAt?: string | null;
  needsReconnect?: boolean;
  error?: string;
}

interface GoogleCalendarTestResponse {
  success: boolean;
  message: string;
  calendarsFound?: number;
  calendars?: Array<{ id: string; summary: string; primary?: boolean }>;
  userEmail?: string;
  userAuthenticated?: boolean;
}

class GoogleCalendarService {
  private createGoogleMeeting = httpsCallable<CreateMeetingRequest, GoogleMeetResponse>(functions, 'createGoogleMeeting');
  private updateGoogleMeeting = httpsCallable<UpdateMeetingRequest, GoogleMeetResponse>(functions, 'updateGoogleMeeting');
  private deleteGoogleMeeting = httpsCallable<DeleteMeetingRequest, { success: boolean; message: string }>(functions, 'deleteGoogleMeeting');
  private testGoogleCalendarIntegration = httpsCallable<{}, GoogleCalendarTestResponse>(functions, 'testGoogleCalendarIntegration');
  private getGoogleAuthUrl = httpsCallable<{ returnUrl?: string }, { url: string }>(functions, 'getGoogleAuthUrl');
  private getGoogleCalendarStatus = httpsCallable<{}, GoogleCalendarStatus>(functions, 'getGoogleCalendarStatus');
  private disconnectGoogleCalendar = httpsCallable<{}, { success: boolean }>(functions, 'disconnectGoogleCalendar');

  /** Összekapcsolás indítása: a Google consent oldalra irányít, onnan a függvény visszahoz az appba */
  async connect(): Promise<void> {
    // Oda jöjjön vissza, ahol most vagyunk (localhost vagy éles, a nyitott oldallal együtt)
    const returnUrl = `${window.location.origin}${window.location.pathname}`;
    const result = await this.getGoogleAuthUrl({ returnUrl });
    window.location.href = result.data.url;
  }

  async status(): Promise<GoogleCalendarStatus> {
    try {
      const result = await this.getGoogleCalendarStatus({});
      return result.data;
    } catch (error: any) {
      return { configured: false, connected: false, error: `${error?.code || ''} ${error?.message || error}`.trim() };
    }
  }

  async disconnect(): Promise<boolean> {
    const result = await this.disconnectGoogleCalendar({});
    return result.data.success;
  }

  /**
   * Ellenőrzi, hogy a Google Calendar integráció működik-e
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      // Ellenőrizzük, hogy a felhasználó be van-e jelentkezve
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        console.log('User not authenticated');
        return false;
      }

      // Kérjük el a felhasználó access token-jét
      const idToken = await currentUser.getIdToken();
      
      const result = await this.testGoogleCalendarIntegration({ 
        userToken: idToken 
      });
      return result.data.success;
    } catch (error) {
      console.error('Google Calendar authentication check failed:', error);
      return false;
    }
  }

  /**
   * Google Calendar esemény létrehozása Meet linkkel
   */
  async createEventWithMeet(appointment: Appointment, organizerEmail: string): Promise<{
    eventId: string;
    meetLink: string;
    calendarLink: string;
  } | null> {
    try {
      const request: CreateMeetingRequest = {
        appointmentId: appointment.id!,
        organizationId: appointment.organizationId!,
        clientName: appointment.clientName,
        clientEmail: appointment.clientEmail,
        organizerEmail: organizerEmail,
        appointmentDate: appointment.appointmentDate.toISOString(),
        duration: appointment.duration || 60,
        title: `Konzultáció - ${appointment.clientName}`,
        clientPhone: appointment.clientPhone,
        clientMessage: appointment.clientMessage
      };

      const result = await this.createGoogleMeeting(request);
      
      if (result.data.success) {
        return {
          eventId: result.data.eventId,
          meetLink: result.data.meetLink,
          calendarLink: result.data.calendarLink
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error creating Google Calendar event:', error);
      throw error;
    }
  }

  /**
   * Google Calendar esemény frissítése
   */
  async updateEvent(eventId: string, appointment: Appointment, organizerEmail: string): Promise<{
    meetLink: string;
    calendarLink: string;
  } | null> {
    try {
      const request: UpdateMeetingRequest = {
        appointmentId: appointment.id!,
        calendarEventId: eventId,
        organizerEmail: organizerEmail,
        clientName: appointment.clientName,
        clientEmail: appointment.clientEmail,
        appointmentDate: appointment.appointmentDate.toISOString(),
        duration: appointment.duration || 60,
        title: `Konzultáció - ${appointment.clientName}`,
        clientPhone: appointment.clientPhone,
        clientMessage: appointment.clientMessage
      };

      const result = await this.updateGoogleMeeting(request);
      
      if (result.data.success) {
        return {
          meetLink: result.data.meetLink,
          calendarLink: result.data.calendarLink
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error updating Google Calendar event:', error);
      throw error;
    }
  }

  /**
   * Google Calendar esemény törlése
   */
  async deleteEvent(eventId: string, organizerEmail: string): Promise<boolean> {
    try {
      const request: DeleteMeetingRequest = {
        calendarEventId: eventId,
        organizerEmail: organizerEmail
      };

      const result = await this.deleteGoogleMeeting(request);
      return result.data.success;
    } catch (error) {
      console.error('Error deleting Google Calendar event:', error);
      return false;
    }
  }

  /**
   * Google Calendar integráció tesztelése
   */
  async testIntegration(): Promise<GoogleCalendarTestResponse> {
    try {
      // Ellenőrizzük, hogy a felhasználó be van-e jelentkezve
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        return {
          success: false,
          message: 'User not authenticated'
        };
      }

      // Egyszerű service account alapú integráció
      // A felhasználó megosztja a naptárait a service account-tal
      const result = await this.testGoogleCalendarIntegration({});
      return {
        success: result.data.success,
        message: result.data.message,
        calendarsFound: result.data.calendarsFound,
        calendars: result.data.calendars,
        userEmail: result.data.userEmail
      };
    } catch (error) {
      console.error('Google Calendar integration test failed:', error);
      return {
        success: false,
        message: `Integration test failed: ${error}`
      };
    }
  }
}

// Singleton instance
export const googleCalendarService = new GoogleCalendarService();
